import { Alert, Snackbar } from '@mui/material'
import { fromUnixTime } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HeaderProformaInvoice, LoadingNormal, ProformaInvoice } from '~/src/components/organisms'
import { ProtectedPage } from '~/src/components/templates'
import { useInvoices, UseInvoicesParams, useUsers } from '~/src/hooks/useApi'
import { useLocation, useNavigate, createSearchParams } from 'react-router-dom'

export const ProformaInvoiceList: React.VFC = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useState<UseInvoicesParams>(
    // @ts-ignore
    location.state && location.state.prevState
    ? // @ts-ignore
      (location.state.prevState as UseInvoicesParams)
    : location.state
    ? (location.state as UseInvoicesParams)
    : ({} as UseInvoicesParams)
  )
  const { data: invoices, error: invoicesError } = useInvoices(params)
  const { data: users, error: usersError } = useUsers({ limit: 1000000 }) // 一回で全て取得するために適当に大きな数字
  const [openSnackBar, setOpenSnackBar] = useState(false)
  const [error, setError] = useState('')
  const [searchId, setSearchId] = useState<{ id: number }>(location.state as {id: number})

  useEffect(() => {
    if (invoicesError) {
      setError(invoicesError.message)
      setOpenSnackBar(true)
      return
    }
    if (usersError) {
      setError(usersError.message)
      setOpenSnackBar(true)
      return
    }
  }, [invoicesError, usersError])

  if (!invoices || !users) {
    return <LoadingNormal />
  }

  const handleSearchId =  (userId: number) => {
    setSearchId({id: userId})
  }

  const handleSearch = async (values: {
    keyword?: string
    userId?: number
  }) => {
    setParams(values)
    // pageを1に戻す
    navigate(
      {
        pathname: location.pathname,
        // @ts-ignore
        search: `?${createSearchParams(values)}`,
      },
      { replace: true, state: values }
    )
  }

  const fetchProformaInvoice = async (_params?: UseInvoicesParams): Promise<void> => {
    let mergedParams = _params
    if (location.state) {
      // @ts-ignore
      mergedParams = { ...location.state, ..._params }
    }
    // @ts-ignore
    setParams(mergedParams)
    navigate(
      {
        pathname: location.pathname,
        // @ts-ignore
        search: `?${createSearchParams(mergedParams)}`,
      },
      { state: mergedParams }
    )
  }
  
  return (
    <ProtectedPage pageTitle={t('list_proforma_invoice.invoice_list')}>
      <Snackbar
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', ml: 16 }}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackBar}
        onClose={() => setOpenSnackBar(false)}
        message={error}
        key={error + 'verticalHorizontal'}
      >
        <Alert onClose={() => setOpenSnackBar(false)} severity="error" sx={{ width: '500px' }}>
          {error}
        </Alert>
      </Snackbar>
      <HeaderProformaInvoice
        onSubmit={handleSearch}
        options={users.items.map((user) => ({ id: user.id!, name: user.name || 'no name' }))}
        onFocus={console.log}
        searchId={searchId?.id}
        handleSearchId={handleSearchId}
      />
      <ProformaInvoice
        invoices={
          // API定義が基本Optionalになっているのでnullチェックを任意で行わない
          invoices.items!.map((item) => ({
            id: item.id.toString(),
            invoiceNo: item.id.toString(),
            billCode: item.billCode!.toString(),
            invoiceType: item.type!,
            companyName: item.user!.companyName!,
            customer: item.user!.name!,
            dateOfCreation: fromUnixTime(item.createdAt!).toLocaleDateString(),
            status: item.status,
            paymentProof: item.paymentProof && {
              filename: item.paymentProof.filename!,
              url: item.paymentProof.url!,
            },
            invoice: item.invoice && {
              filename: item.invoice.filename!,
              url: item.invoice.url!,
            },
          }))
        }
        pagination={invoices.pagination!}
        onSubmitGetInvoice={fetchProformaInvoice}
      />
    </ProtectedPage>
  )
}
